import { inject, Pipe, PipeTransform } from '@angular/core';
import { UserInfoResolver, UserPreferencesHelper } from '@miq/platform';
import { LocaleId } from '@miq/shared-models';
import { isMasked } from '@miq/utils';
import moment from 'moment-timezone';

enum MomentTimeZoneDisplayFormats {
  timeToSeconds = 'timeToSeconds',
  timeWithDate = 'timeWithDate',
  timeWithJustMins = 'timeWithJustMins',
  timeWithDateAlter = 'timeWithDateAlter',
}
type MomentTimeZoneDisplayStrings = keyof typeof MomentTimeZoneDisplayFormats;

@Pipe({ name: 'momentTimezone' })
export class MomentTimezonePipe implements PipeTransform {
  private readonly userInfoResolver = inject(UserInfoResolver);

  transform(
    value: string | Date | moment.Moment,
    format: MomentTimeZoneDisplayStrings = MomentTimeZoneDisplayFormats.timeToSeconds
  ): string {
    const date = moment.isMoment(value) ? value : moment(value, ['MM/DD/YYYY HH:mm:ss z', 'YYYY-MM-DD HH:mm:ss z']);
    return isMasked(value)
      ? (value as string)
      : moment.tz(date, moment.tz.guess()).format(this.formatString(format));
  }

  private formatString(format: MomentTimeZoneDisplayStrings): string {
    // Resolved per call (the host version froze these at module-eval time, before user
    // preferences ever loaded); the saved global date preference wins, otherwise the
    // fallback derives from the user's locale — same order as the host helper.
    switch (format) {
      case MomentTimeZoneDisplayFormats.timeWithJustMins:
        return 'HH:mm:ss z';
      case MomentTimeZoneDisplayFormats.timeWithDateAlter:
        return 'MM/D/YYYY';
      case MomentTimeZoneDisplayFormats.timeWithDate:
        return this.globalDateDisplayFormat();
      case MomentTimeZoneDisplayFormats.timeToSeconds:
      default:
        return this.globalDateDisplayFormat() + ' HH:mm:ss z';
    }
  }

  private globalDateDisplayFormat(): string {
    const opts = UserPreferencesHelper.userViewOptions;
    if (
      opts == null ||
      opts.globalDateFormat == null ||
      (Array.isArray(opts.globalDateFormat) && !opts.globalDateFormat.length)
    ) {
      UserPreferencesHelper.getUserViewOptions();
    }
    const { globalDateFormat } = UserPreferencesHelper.userViewOptions;
    const dateOpt = !!globalDateFormat && globalDateFormat.length ? globalDateFormat[0] : null;
    if (!!dateOpt) {
      return dateOpt['value'];
    }
    // No saved preference: derive the default from the user's locale (US when unknown).
    const userInfo = this.userInfoResolver.resolve();
    const usFormat = !userInfo || userInfo.userLocaleId === LocaleId.US;
    return usFormat ? 'MM/DD/YYYY' : 'DD/MM/YYYY';
  }
}
